import { type FC, useCallback, useMemo } from 'react';
import { HttpError, useDataProvider } from 'ra-core';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { bulkUpdateUserSchema, DATE_FORMATS } from './types';
import { format, isValid, parse } from 'date-fns';
import { BulkOperation } from '../BulkOperation/BulkOperation';
import {
  type GridRowValidationError,
  type GridRowValidator,
  type RowProcessor,
} from '../BulkOperation/types';
import { z } from 'zod';
import { superRefineDuplicateProps } from '../BulkOperation/bulkZodUtils';
import { getErrorMessage } from './getErrorMessage';
import { getAdminUserDisplayName } from './types';

const exampleValues = [
  // Any omitted columns will use existing values.
  // This also means that values cannot be removed via bulk update.
  {
    internalId: 'example-id',
    firstName: 'John',
    lastName: 'Smith',
    phoneNumber: '+40123456789',
    startDate: format(new Date(), 'dd-MM-yyyy'),
    department: 'HR',
    jobTitle: 'Manager',
    region: 'UK',
    unit: 'London',
    languageCountryCode: 'en',
    // We could add the picture URL here, but it's not necessary for the CSV import
    // Similarly, we omit id. This is because it's unclear to the user what would happen when you set both id and internalId
  },
] as const satisfies z.infer<typeof bulkUpdateUserSchema>[];

export const BulkUpdateUsersButton: FC = () => {
  const userUpdateSchema = useMemo(
    () =>
      bulkUpdateUserSchema.superRefine((data, ctx) => {
        if (!(data.id || data.internalId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Either 'id' or 'internalId' must be provided.",
            path: ['id'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Either 'id' or 'internalId' must be provided.",
            path: ['internalId'],
          });
        }
      }),
    []
  );

  const csvSchema = useMemo(
    () =>
      z
        .array(userUpdateSchema)
        .superRefine((data, ctx) =>
          superRefineDuplicateProps(
            'id',
            data.filter((d) => !!d.id),
            ctx
          )
        )
        .superRefine((data, ctx) =>
          superRefineDuplicateProps(
            'internalId',
            data.filter((d) => !!d.internalId),
            ctx
          )
        ),
    [userUpdateSchema]
  );

  const dataProvider = useDataProvider<AdminDataProvider>();

  const toUtcIsoString = useCallback((dateString: string): string => {
    const parsedDate = DATE_FORMATS.map((dateFormat) =>
      parse(dateString, dateFormat, new Date())
    ).find((date) => isValid(date));

    if (!parsedDate) {
      throw new Error(`Invalid date format: ${dateString}`);
    }

    return new Date(
      Date.UTC(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate()
      )
    ).toISOString();
  }, []);

  const validateRow = useCallback<
    GridRowValidator<z.infer<typeof userUpdateSchema>>
  >(
    async ({ data: user }) => {
      const userLookup = await dataProvider
        .getUser({
          id: user.id,
          internalId: user.internalId,
        })
        .catch((error: unknown) => ({ error }));

      if (userLookup && 'error' in userLookup) {
        return (oldData) => ({
          ...oldData,
          status: 'error',
          data: user,
          asyncErrors: [
            {
              message: getErrorMessage(userLookup.error),
              property: user.internalId ? 'internalId' : 'id',
            },
          ],
        });
      }

      if (!userLookup) {
        if (user.internalId) {
          return (oldData) => ({
            ...oldData,
            status: 'error',
            data: user,
            asyncErrors: [
              {
                message: "User with this internal id doesn't exist.",
                property: 'internalId',
              },
            ],
          });
        } else {
          return (oldData) => ({
            ...oldData,
            status: 'error',
            data: user,
            asyncErrors: [
              {
                message: "User with this id doesn't exist.",
                property: 'id',
              },
            ],
          });
        }
      } else {
        const phoneLookup = await dataProvider
          .getUserByPhoneNumber(user.phoneNumber)
          .catch((error: unknown) => ({ error }));

        const errors: GridRowValidationError<
          z.infer<typeof userUpdateSchema>
        >[] = [];
        if (phoneLookup && 'error' in phoneLookup) {
          errors.push({
            message: getErrorMessage(phoneLookup.error),
            property: 'phoneNumber',
          });
        } else if (phoneLookup && phoneLookup.id !== userLookup.id) {
          errors.push({
            message: `This phone number is already taken by user: ${getAdminUserDisplayName(phoneLookup)}`,
            property: 'phoneNumber',
          });
        }

        return (oldData) => ({
          ...oldData,
          status: errors.length > 0 ? 'error' : 'success',
          asyncErrors: errors,
          data: {
            ...user,
            id: user.internalId ? userLookup.id.toString() : user.id,
          },
        });
      }
    },
    [dataProvider]
  );

  const processRow = useCallback<
    RowProcessor<z.infer<typeof userUpdateSchema>>
  >(
    async (row) => {
      const response = await dataProvider
        .update('users', {
          id: row.data.id,
          data: {
            ...row.data,
            startDate: row.data.startDate
              ? toUtcIsoString(row.data.startDate)
              : null,
          },
          previousData: undefined,
        })
        .catch((err: unknown) => {
          if (err instanceof HttpError) {
            // NOTE: HttpError won't return the response body, as it isn't JSON, so it ignores it
            // https://github.com/marmelab/react-admin/blob/0a7a6e7f612990dca093fbbf4cfa531ead4583a2/packages/ra-core/src/dataProvider/fetch.ts#L67
            return err;
          }

          throw err;
        });

      if (response instanceof HttpError) {
        return (oldData) => ({
          ...oldData,
          status: 'error',
          data: row.data,
          errors: [
            response.body?.message ||
              'Unexpected error. Please reach out to Moonstar Support.',
          ],
        });
      } else {
        return (oldData) => ({
          ...oldData,
          status: 'success',
          data: row.data,
        });
      }
    },
    [dataProvider, toUtcIsoString]
  );

  return (
    <BulkOperation
      label="moonstar.bulk_update.label"
      resource="users"
      schema={csvSchema}
      exampleValues={exampleValues}
      validateRow={validateRow}
      processRow={processRow}
      // due to clerk's 20reqs/10s limit
      validationRateLimitDelay={500}
      processingRateLimitDelay={500}
    />
  );
};
